// Periodization and Target-plan timeline rows shown inside CalendarHeader.
// PhaseRow renders training-phase spans from the annual planner (diet phases
// are absent from the model. PlanTimelineRow renders nutrition-plan
// spans proportional to their day count in the visible range.
// Both accept pre-computed data so the expensive calls stay in CalendarView's
// useMemo and are not re-run on every render.

import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { getPlanTargetsSummary } from "../../../../../utils/nutrition-progress";
import { fmtUtc, translateCalendarHeader } from "./viewTokens";
import { Dot } from "./viewAtoms";
// Calculates the number of days between two UTC dates
const dayCount = (startUtc, endUtc) =>
  moment(String(endUtc), "YYYYMMDD").diff(moment(String(startUtc), "YYYYMMDD"), "days") + 1;

// Width of a span/plan pill, proportional to how many days it covers within
// the visible range — encodes duration visually (a 20-day phase reads wider
// than a 5-day one).
const pctOfRange = (startUtc, endUtc, fromUtc, toUtc) =>
  (dayCount(startUtc, endUtc) / dayCount(fromUtc, toUtc)) * 100;

const spanShape = PropTypes.shape({
  name: PropTypes.string,
  color: PropTypes.string,
  startUtc: PropTypes.number,
  endUtc: PropTypes.number,
});

/* ---- shared row wrapper: label + track of pills ------------------------ */

const TimelineRow = ({ label, children }) => (
  <div className="np-cal-timeline-row">
    <span className="np-cal-timeline-row__label">{label}</span>
    <div className="np-cal-timeline-row__track">{children}</div>
  </div>
);

TimelineRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/* ---- training phase row ---------------------------------------------- */

export const PhaseRow = ({ label, spans, fromUtc, toUtc }) => {
  if (!spans.length) return null;

  return (
    <TimelineRow label={label}>
      {spans.map((span) => (
        <div
          key={span.startUtc}
          className="np-cal-timeline-pill"
          style={{
            flexBasis: `${pctOfRange(span.startUtc, span.endUtc, fromUtc, toUtc)}%`,
            // Same hex color drives both: solid on the border for a vivid accent,
            // with a "33" alpha suffix (#RRGGBBAA, ~20% opacity) on the background
            // so it reads as a muted tint of that same color.
            background: span.color ? `${span.color}33` : "var(--white-10)",
            borderLeft: `4px solid ${span.color || "var(--white-20)"}`,
          }}
        >
          <p className="np-cal-timeline-pill__title">{span.name}</p>
          <p className="np-cal-timeline-pill__sub">
            {fmtUtc(span.startUtc)} – {fmtUtc(span.endUtc)}
          </p>
        </div>
      ))}
    </TimelineRow>
  );
};

PhaseRow.propTypes = {
  label: PropTypes.string.isRequired,
  spans: PropTypes.arrayOf(spanShape).isRequired,
  fromUtc: PropTypes.number.isRequired,
  toUtc: PropTypes.number.isRequired,
};

/* ---- target plans row -------------------------------------------------- */

// Kcal + meal-plan summary line for a plan pill, as an array of React nodes
// (not a plain string) so the " · " separators can render as the bigger,
// bolder <Dot/> atom instead of inheriting the small pill sub-text size.
// Omits any segment (training, rest, meal plan) that has no data instead of
// showing a "—" placeholder.
const buildPlanSummaryNodes = (summary, t) => {
  const kcalSegments = [];
  if (summary.trainingKcal != null) {
    kcalSegments.push(translateCalendarHeader(t, "trainingSegment", { value: summary.trainingKcal.toLocaleString() }));
  }
  if (summary.restKcal != null) {
    kcalSegments.push(translateCalendarHeader(t, "restSegment", { value: summary.restKcal.toLocaleString() }));
  }

  const groups = [];
  if (kcalSegments.length) {
    groups.push({
      key: "kcal",
      content: (
        <>
          {kcalSegments.map((segment, i) => (
            <React.Fragment key={segment}>
              {i > 0 && <Dot />}
              {segment}
            </React.Fragment>
          ))}
          {" kcal"}
        </>
      ),
    });
  }
  if (summary.hasMeal) {
    groups.push({ key: "meal", content: translateCalendarHeader(t, "mealPlan") });
  }

  return groups.length
    ? groups.map((group, i) => (
        <React.Fragment key={group.key}>
          {i > 0 && <Dot />}
          {group.content}
        </React.Fragment>
      ))
    : null;
};

export const PlanTimelineRow = ({ label, plans, fromUtc, toUtc }) => {
  const { t } = useTranslation("athlete-portal");
  if (!plans.length) return null;

  return (
    <TimelineRow label={label}>
      {plans.map((plan) => {
        const start = Math.max(plan.utcStartDate, fromUtc);
        const end = Math.min(plan.utcEndDate, toUtc);
        const summary = getPlanTargetsSummary(plan);
        const subNodes = summary && buildPlanSummaryNodes(summary, t);
        return (
          <div
            key={String(plan._id)}
            className="np-cal-timeline-pill np-cal-timeline-pill--plan"
            style={{
              flexBasis: `${pctOfRange(start, end, fromUtc, toUtc)}%`,
              borderLeft: "4px solid var(--gradient-right)",
            }}
          >
            <p className="np-cal-timeline-pill__title">
              {summary ? summary.label : `${fmtUtc(start)} – ${fmtUtc(end)}`}
            </p>
            {subNodes && <p className="np-cal-timeline-pill__sub">{subNodes}</p>}
          </div>
        );
      })}
    </TimelineRow>
  );
};

PlanTimelineRow.propTypes = {
  label: PropTypes.string.isRequired,
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
  fromUtc: PropTypes.number.isRequired,
  toUtc: PropTypes.number.isRequired,
};
